import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface VoiceRoom {
  id: string;
  name: string;
  topic: string | null;
  max_participants: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

interface VoiceParticipant {
  id: string;
  room_id: string;
  user_id: string;
  is_speaking: boolean;
  has_revealed_photo: boolean;
  reveal_requested_by: string[];
  joined_at: string;
  left_at: string | null;
  profile?: {
    id: string;
    name: string;
    photo_url: string | null;
    bio: string | null;
  };
}

interface VoiceLike {
  id: string;
  room_id: string;
  from_user_id: string;
  to_user_id: string;
  is_mutual: boolean;
  created_at: string;
}

export const useVoiceDiscovery = (roomId?: string) => {
  const { user } = useAuthContext();
  const [rooms, setRooms] = useState<VoiceRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<VoiceRoom | null>(null);
  const [participants, setParticipants] = useState<VoiceParticipant[]>([]);
  const [myLikes, setMyLikes] = useState<VoiceLike[]>([]);
  const [likesReceived, setLikesReceived] = useState<VoiceLike[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch active voice rooms
  const fetchRooms = useCallback(async () => {
    const { data, error } = await supabase
      .from("voice_discovery_rooms")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (data && !error) {
      setRooms(data);
    }
    setLoading(false);
  }, []);

  // Create a new voice room
  const createRoom = useCallback(
    async (name: string, topic?: string) => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("voice_discovery_rooms")
        .insert({
          name,
          topic: topic || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        toast.error("Failed to create room");
        return null;
      }

      toast.success("Voice room created!");
      return data;
    },
    [user]
  );

  // Join a voice room
  const joinRoom = useCallback(
    async (targetRoomId: string) => {
      if (!user) return false;

      // First check if already in room
      const { data: existing } = await supabase
        .from("voice_room_participants")
        .select("id")
        .eq("room_id", targetRoomId)
        .eq("user_id", user.id)
        .is("left_at", null)
        .single();

      if (existing) {
        // Already in room
        return true;
      }

      const { error } = await supabase.from("voice_room_participants").insert({
        room_id: targetRoomId,
        user_id: user.id,
      });

      if (error) {
        toast.error("Failed to join room");
        return false;
      }

      return true;
    },
    [user]
  );

  // Leave a voice room
  const leaveRoom = useCallback(
    async (targetRoomId: string) => {
      if (!user) return;

      const { error } = await supabase
        .from("voice_room_participants")
        .update({ left_at: new Date().toISOString() })
        .eq("room_id", targetRoomId)
        .eq("user_id", user.id)
        .is("left_at", null);

      if (error) {
        console.error("Failed to leave room:", error);
      }
    },
    [user]
  );

  // Toggle speaking status
  const toggleSpeaking = useCallback(
    async (isSpeaking: boolean) => {
      if (!user || !roomId) return;

      const { error } = await supabase
        .from("voice_room_participants")
        .update({ is_speaking: isSpeaking })
        .eq("room_id", roomId)
        .eq("user_id", user.id);

      if (error) {
        console.error("Failed to update speaking status:", error);
      }
    },
    [user, roomId]
  );

  // Request photo reveal from someone
  const requestReveal = useCallback(
    async (targetUserId: string) => {
      if (!user || !roomId) return;

      // Get current participant
      const participant = participants.find((p) => p.user_id === targetUserId);
      if (!participant) return;

      // Add our ID to the reveal requests
      const currentRequests = participant.reveal_requested_by || [];
      if (currentRequests.includes(user.id)) return;

      const { error } = await supabase
        .from("voice_room_participants")
        .update({
          reveal_requested_by: [...currentRequests, user.id],
        })
        .eq("id", participant.id);

      if (error) {
        toast.error("Failed to request reveal");
      } else {
        toast.success("Photo reveal requested!");
      }
    },
    [user, roomId, participants]
  );

  // Reveal own photo
  const revealPhoto = useCallback(async () => {
    if (!user || !roomId) return;

    const { error } = await supabase
      .from("voice_room_participants")
      .update({ has_revealed_photo: true })
      .eq("room_id", roomId)
      .eq("user_id", user.id);

    if (error) {
      toast.error("Failed to reveal photo");
    } else {
      toast.success("Photo revealed!");
    }
  }, [user, roomId]);

  // Send a voice-based like
  const sendVoiceLike = useCallback(
    async (toUserId: string) => {
      if (!user || !roomId) return;

      // Check if already liked
      const existing = myLikes.find((l) => l.to_user_id === toUserId);
      if (existing) {
        toast.info("Already liked this person!");
        return;
      }

      // Check if they already liked us (for mutual)
      const theyLiked = likesReceived.find((l) => l.from_user_id === toUserId);

      const { data, error } = await supabase
        .from("voice_likes")
        .insert({
          room_id: roomId,
          from_user_id: user.id,
          to_user_id: toUserId,
          is_mutual: !!theyLiked,
        })
        .select()
        .single();

      if (error) {
        toast.error("Failed to send like");
        return;
      }

      // Update their like to mutual if they liked us first
      if (theyLiked) {
        await supabase
          .from("voice_likes")
          .update({ is_mutual: true })
          .eq("id", theyLiked.id);

        toast.success("It's a match! You both like each other's vibe! 🎉");
      } else {
        toast.success("Like sent based on their voice!");
      }
    },
    [user, roomId, myLikes, likesReceived]
  );

  // Initial fetch
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Fetch room data when roomId changes
  useEffect(() => {
    if (!roomId || !user) {
      setCurrentRoom(null);
      setParticipants([]);
      return;
    }

    const fetchRoomData = async () => {
      // Fetch room
      const { data: roomData } = await supabase
        .from("voice_discovery_rooms")
        .select("*")
        .eq("id", roomId)
        .single();

      if (roomData) {
        setCurrentRoom(roomData);
      }

      // Fetch participants with profiles
      const { data: participantsData } = await supabase
        .from("voice_room_participants")
        .select(`
          *,
          profile:profiles!voice_room_participants_user_id_fkey (id, name, photo_url, bio)
        `)
        .eq("room_id", roomId)
        .is("left_at", null);

      if (participantsData) {
        setParticipants(participantsData as unknown as VoiceParticipant[]);
      }

      // Fetch my likes
      const { data: myLikesData } = await supabase
        .from("voice_likes")
        .select("*")
        .eq("room_id", roomId)
        .eq("from_user_id", user.id);

      if (myLikesData) {
        setMyLikes(myLikesData);
      }

      // Fetch likes received
      const { data: receivedData } = await supabase
        .from("voice_likes")
        .select("*")
        .eq("room_id", roomId)
        .eq("to_user_id", user.id);

      if (receivedData) {
        setLikesReceived(receivedData);
      }
    };

    fetchRoomData();

    // Subscribe to participant changes
    const channel = supabase
      .channel(`voice-room-${roomId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voice_room_participants",
          filter: `room_id=eq.${roomId}`,
        },
        async () => {
          // Refetch participants
          const { data } = await supabase
            .from("voice_room_participants")
            .select(`
              *,
              profile:profiles!voice_room_participants_user_id_fkey (id, name, photo_url, bio)
            `)
            .eq("room_id", roomId)
            .is("left_at", null);

          if (data) {
            setParticipants(data as unknown as VoiceParticipant[]);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "voice_likes",
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          const like = payload.new as VoiceLike;
          if (like.from_user_id === user.id) {
            setMyLikes((prev) => [...prev, like]);
          }
          if (like.to_user_id === user.id) {
            setLikesReceived((prev) => [...prev, like]);
            if (like.is_mutual) {
              toast.success("Mutual voice match! 🎉");
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, user]);

  // Check if I've liked someone
  const hasLiked = useCallback(
    (userId: string) => myLikes.some((l) => l.to_user_id === userId),
    [myLikes]
  );

  // Check if someone liked me
  const hasLikedMe = useCallback(
    (userId: string) => likesReceived.some((l) => l.from_user_id === userId),
    [likesReceived]
  );

  // Check if mutual
  const isMutualMatch = useCallback(
    (userId: string) => {
      const myLike = myLikes.find((l) => l.to_user_id === userId);
      return myLike?.is_mutual || false;
    },
    [myLikes]
  );

  return {
    rooms,
    currentRoom,
    participants,
    loading,
    createRoom,
    joinRoom,
    leaveRoom,
    toggleSpeaking,
    requestReveal,
    revealPhoto,
    sendVoiceLike,
    hasLiked,
    hasLikedMe,
    isMutualMatch,
    refetchRooms: fetchRooms,
  };
};
